import React from 'react';
import { Link } from 'react-router-dom';
import Legal from '../../components/layouts/legal';

const PrivacyPolicy = () => (
  <Legal title="Privacy Policy" lastUpdated="October 19, 2026">
    <p>
      At ADM CPA P.C. ("ADM CPA", "we", "us"), we value your privacy and are committed to
      protecting your personal information. This Privacy Policy explains how we collect, use,
      and safeguard your data when you use the ADM Client Portal (https://cp.admone.app), our
      website (https://admcpapc.com), and when we communicate with you by text message (SMS),
      email, or WhatsApp.
    </p>

    <h2>Information We Collect</h2>
    <ul>
      <li><strong>Account Information:</strong> name, email address, mobile phone number, and login credentials for your Client Portal account.</li>
      <li><strong>Client Records:</strong> documents, company details, and messages you upload to or exchange with us through the Client Portal so we can provide accounting and tax services.</li>
      <li><strong>Contact Form Data:</strong> name, email address, phone number, and any other details you voluntarily provide.</li>
      <li><strong>Messaging Data:</strong> your phone number, message history, and messaging preferences when we contact you by SMS or WhatsApp.</li>
      <li><strong>Technical Information:</strong> IP address, browser type, and basic usage data to keep the portal secure and improve its performance.</li>
    </ul>
    <p>
      All the above categories exclude text messaging originator opt-in data and consent; this
      information won’t be shared with any third parties.
    </p>

    <h2>How We Use Your Information</h2>
    <ul>
      <li>Providing the services you engage us for and responding to your inquiries.</li>
      <li>Securing your account, including sending one-time verification codes.</li>
      <li>Sending service notifications such as document requests, task reminders, updates, and confirmations by SMS, email, or WhatsApp.</li>
      <li>Improving the Client Portal, our website, and customer support.</li>
      <li>Complying with legal and professional obligations.</li>
    </ul>
    <p>We do not sell or rent your personal data to third parties.</p>

    <h2>Text Messaging (SMS)</h2>
    <p>
      If you provide your mobile phone number and agree to receive text messages, we will use it
      only to send the account and service messages described in
      our <Link to="/terms">Terms &amp; Conditions</Link>. No mobile information will be shared
      with third parties or affiliates for marketing or promotional purposes. Your SMS opt-in
      data and consent are never shared with any third party.
    </p>
    <p>
      You can opt out at any time by replying <strong>STOP</strong> to any message. Reply
      <strong> HELP</strong> for help. Message and data rates may apply.
    </p>

    <h2>WhatsApp &amp; Meta Business API</h2>
    <p>
      We use Meta’s WhatsApp Business API to send important, utility-based communications to our
      clients. Messages are end-to-end encrypted as per WhatsApp’s security framework, and Meta may
      process limited data in accordance with WhatsApp’s Privacy Policy.
    </p>

    <h2>Use of Artificial Intelligence</h2>
    <p>
      We use artificial intelligence tools to help our team work more efficiently, for example to
      suggest names and categories for documents you upload and to help our staff draft clear
      messages. These tools run on Amazon Web Services (Amazon Bedrock) within the United States.
      Your information is processed only to perform that task. It is not used to train AI models
      and is not shared with the AI provider for any other purpose.
    </p>
    <p>
      AI does not make decisions about you. Every AI suggestion is reviewed by a member of our team,
      and all accounting, tax, and advisory work is performed and reviewed by qualified
      professionals. AI tools are never used on your text messaging opt-in data or consent.
    </p>
    <p>
      If you have questions about how AI is used with your information, contact us
      at <a href="mailto:info@admcpapc.com">info@admcpapc.com</a>.
    </p>

    <h2>Data Sharing &amp; Security</h2>
    <p>
      We share data only with trusted service providers that help us deliver our services (for
      example, hosting, email, and messaging providers), and only to the extent needed for that
      purpose. These providers may not use your information for their own marketing. We apply
      reasonable technical and organizational measures to protect your information from
      unauthorized access or misuse.
    </p>

    <h2>Data Retention</h2>
    <p>
      We retain personal data only as long as necessary to provide our services, comply with legal
      and professional record-keeping obligations, or resolve disputes.
    </p>

    <h2>Your Rights</h2>
    <p>Depending on your jurisdiction, you may have rights to:</p>
    <ul>
      <li>Access, update, or request deletion of your personal data.</li>
      <li>Withdraw consent to receive text messages or WhatsApp communications.</li>
      <li>Lodge a complaint with a data protection authority.</li>
    </ul>

    <h2>Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. The "Last Updated" date above shows when
      it was last revised.
    </p>

    <h2>Contact Us</h2>
    <p>
      If you have questions about this Privacy Policy or your data, please contact us at:
    </p>
    <address>
      ADM CPA P.C.<br />
      Email: <a href="mailto:info@admcpapc.com">info@admcpapc.com</a><br />
      Phone: <a href="tel:+17133373100">(713) 337-3100</a>
    </address>
  </Legal>
);

export default PrivacyPolicy;
